import React, { useState } from 'react';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { SectionHeading } from './SectionHeading';
import { volumes } from '../data/volumes';
/** Topics shown per volume before the "+ N more" line. */
const VISIBLE_TOPICS = 6;
export function VolumesSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section id="volumes" className="defer-paint w-full scroll-mt-6 bg-ink py-10 sm:py-14" aria-labelledby="volumes-heading" data-id="element-238" data-mp="7nbksnmks6g3">
      <div className="mx-auto max-w-4xl px-6" data-id="element-239" data-mp="3c3wbu9swg67e">
        <SectionHeading eyebrow="The complete system" title={<span id="volumes-heading" data-id="element-241" data-mp="3purdp24zdn1h">
              The complete system that organizes your volleyball coaching
            </span>} data-id="element-240" data-mp="3c57fm9mchdyi" />

        <p className="mx-auto mt-3 max-w-xl text-center leading-relaxed text-muted" data-id="element-242" data-mp="3l3j1xzg6hpl5">
          Open any volume and see what to coach, how to run it, and how to
          turn it into real volleyball.
        </p>

        <ol className="mt-10 space-y-3" data-id="element-243" data-mp="2sf3y892hytp4">
          {volumes.map((volume, index) => {
          const isOpen = openIndex === index;
          const topics = volume.highlights.slice(0, VISIBLE_TOPICS);
          const moreCount = volume.highlights.length - topics.length;
          return <li key={volume.code} className="relative overflow-hidden rounded-3xl border transition-colors duration-150 ease-out" style={{
            borderColor: isOpen ? volume.color : `${volume.color}33`,
            backgroundColor: `${volume.color}0a`
          }} data-id="element-244" data-mp="2j6d1fza4p7r9" data-mpi={index}>
                <span className="absolute inset-y-0 left-0 w-1.5" style={{
              backgroundColor: volume.color
            }} aria-hidden="true" data-id="element-245" data-mp="1vf7k1foji7eu" />
                <h3 data-id="element-246" data-mp="20diunsmnsly4">
                  <button type="button" onClick={() => setOpenIndex(isOpen ? null : index)} aria-expanded={isOpen} aria-controls={`volume-panel-${index}`} className="flex w-full items-center gap-4 rounded-3xl py-4 pl-6 pr-5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent sm:gap-5 sm:pl-8 sm:pr-6" data-id="element-247" data-mp="24gfibxr2388x">
                    <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full font-display text-2xl font-extrabold text-white" style={{
                  backgroundColor: volume.color
                }} aria-hidden="true" data-id="element-248" data-mp="1gp9jfcmu230u">
                      {volume.code}
                    </span>
                    <span className="min-w-0 flex-1" data-id="element-249" data-mp="1kqbgbmta22zn">
                      <span className="block font-display text-sm font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-250" data-mp="x3vaiqskhbt6">
                        Volume {volume.code} · {volume.count}
                      </span>
                      <span className="mt-0.5 block font-display text-xl font-bold uppercase leading-tight text-fg sm:text-2xl" data-id="element-251" data-mp="16teszvcdb3ni">
                        {volume.name}
                      </span>
                    </span>
                    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-navy transition-transform duration-200 ease-out ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-252" data-mp="eaiwytkt6tdv">
                      <ChevronDownIcon className="h-4 w-4 text-muted" data-id="element-253" data-mp="2kyhzsvvucs6d" />
                    </span>
                  </button>
                </h3>

                <div id={`volume-panel-${index}`} className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`} data-id="element-254" data-mp="2c0pgzhqwv2e8">
                  <div className="overflow-hidden" data-id="element-255" data-mp="34obwnodexxhg">
                    <div className="pb-5 pl-6 pr-5 sm:pl-8 sm:pr-6" data-id="element-256" data-mp="34mr0xvfd48i0">
                      <p className="text-[0.98rem] font-medium leading-snug text-fg" data-id="element-257" data-mp="38pndp5i81ae1">{volume.intro}</p>

                      <div className="mt-3.5 grid gap-2.5 sm:grid-cols-[1.35fr_1fr]" data-id="element-258" data-mp="2upw21bf6g637">
                        <div className="rounded-2xl px-4 py-3.5" style={{
                      backgroundColor: `${volume.color}14`
                    }} data-id="element-259" data-mp="2zo7cnodaqkmh">
                          <p className="font-display text-xs font-bold uppercase tracking-[0.18em]" style={{
                        color: volume.color
                      }} data-id="element-260" data-mp="1oa292wgzwcro">
                            Inside
                          </p>
                          <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5" data-id="element-261" data-mp="1ysgwppdipe1k">
                            {topics.map(item => <li key={item} className="flex items-start gap-1.5 text-[0.82rem] font-medium leading-snug text-fg" data-id="element-262" data-mp="1us6nm3ufi7ih">
                                <span className="mt-[0.4rem] h-1.5 w-1.5 shrink-0 rounded-full" style={{
                            backgroundColor: volume.color
                          }} aria-hidden="true" data-id="element-263" data-mp="1pqr3qf3zm71o" />
                                {item}
                              </li>)}
                          </ul>
                          {moreCount > 0 ? <p className="mt-2 text-[0.78rem] font-semibold text-muted" data-id="element-264" data-mp="28juzq74ogvzg">
                              + {moreCount} more topics
                            </p> : null}
                        </div>

                        <div className="flex flex-col rounded-2xl bg-navy px-4 py-3.5 ring-1 ring-line" data-id="element-265" data-mp="x2v87dq5ml6h">
                          <p className="flex items-center gap-1.5 font-display text-xs font-bold uppercase tracking-[0.18em] text-accent" data-id="element-266" data-mp="n1as8e8322tr">
                            <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" data-id="element-267" data-mp="rwq450mj5onj" />
                            Result
                          </p>
                          <p className="mt-2 text-[0.85rem] font-medium leading-snug text-fg" data-id="element-268" data-mp="11pnolcb31nos">
                            {volume.result}
                          </p>
                          {volume.note ? <p className="mt-auto pt-3 text-[0.78rem] font-semibold" style={{
                        color: volume.color
                      }} data-id="element-269" data-mp="1aoiyaydc5fwu">
                              {volume.note}
                            </p> : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </li>;
        })}
        </ol>
      </div>
    </section>;
}